// =============================================================================
// forecastSignalsCore.js — assembling the two LIVE signals (bank holidays + weather)
// into the engine config, with every bit of IO injectable. Lane BQ, Run #14.
//
// PURE by default: nothing here touches the network unless you hand it a `rpc` or a
// `fetchDay`. The browser binding (forecastSignals.js) supplies the real Supabase
// client; the parity tool (tools/lane_bq_forecast_parity.mjs) supplies the staging SQL
// runner; the unit tests supply fakes. All three therefore assemble the config with
// the SAME code, so the screen and the parity check cannot drift apart.
//
// WHY "config": the live engine consults its bank-holiday list in BOTH multiplier
// functions (the target's flags AND the de-normalising of every history / previous-year
// day) and fetches the target's weather once. forecast.js DEFAULT_CONFIG now carries a
// `bankHolidays` calendar (like `schoolHolidays`) and a single date-keyed `weather`
// reading, and contextMultipliers reads them — so the reader passes ONE object and the
// arithmetic path (proven 177/177) is untouched. Empty/null = today's behaviour.
// =============================================================================
import { DEFAULT_CONFIG } from "./forecast.js";
import { holidaysFromFeed, bankHolidayWindow } from "./forecastBankHolidays.js";
import { fetchOpenMeteoDay, weatherForConfig, weatherFromStoredRow } from "./forecastWeather.js";

// The engine config with the live signals attached. Pure.
export function configWithSignals(base = DEFAULT_CONFIG, { bankHolidays = [], weather = null } = {}) {
  const dates = Array.from(new Set((bankHolidays || []).map((d) => String(d).slice(0, 10)))).sort();
  return { ...base, bankHolidays: dates, weather: weather || null };
}

// Weather for ONE target date, resolved the way the reader and the parity tool both
// must: a stored 1321 row first (the nightly importer's reading), else the direct
// open-meteo call the engine makes on every build. Never throws.
//   → { weather: <config object or null>, result: <the raw outcome, for logging> }
export async function resolveWeather({ date, storedRow = null, fetchDay = fetchOpenMeteoDay, now = new Date() }) {
  let result;
  if (storedRow && typeof storedRow.is_rainy === "boolean") {
    result = weatherFromStoredRow(storedRow);
  } else {
    try { result = await fetchDay(date); }
    catch (e) { result = { status: "error", reason: e?.message || String(e) }; }
  }
  return { weather: weatherForConfig(date, result, now), result };
}

// For reporting: which of the bundle's history / previous-year days are bank holidays
// (the days the engine divides +0.20 back out of).
export function bankHolidaysInBundle(bundle, bankHolidays) {
  const set = new Set(bankHolidays || []);
  const hit = [];
  for (const h of (bundle && bundle.history) || []) if (h && set.has(String(h.date).slice(0, 10))) hit.push(h.date);
  for (const p of (bundle && bundle.prev_year) || []) if (p && set.has(String(p.date).slice(0, 10))) hit.push(p.date);
  return hit;
}

// Today's date on the device, ISO. The bank-holiday list is cached per calendar day
// (the engine caches its gov.uk fetch for 24 h).
export function localTodayIso(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
}

// The loader, with injectable IO. `rpc(name, args)` must resolve to { data, error }
// (the supabase-js shape). `cache` is a plain object the browser binding keeps for
// the page's lifetime so the bank-holiday feed is asked once per calendar day.
//
// Outcomes are the engine's own:
//   * bank-holiday feed fails / empty → [] → no bank-holiday flag anywhere (gov.uk down);
//   * weather: stored row → direct call → 'unavailable' chip (≤ 14 days) → nothing.
// Never throws; the worst case is the engine with neither signal — today's reader.
export async function loadForecastSignalsWith({
  site, date, rpc, fetchDay = fetchOpenMeteoDay, now = new Date(), todayIso = localTodayIso(now), cache = {},
  warn = (m) => { try { console.warn(m); } catch (e) { /* no console */ } },
}) {
  // 1. Bank holidays — once per calendar day per page.
  let bankHolidays = [];
  if (cache.bhDay === todayIso && Array.isArray(cache.bhDates)) {
    bankHolidays = cache.bhDates;
  } else {
    const { from, to } = bankHolidayWindow(todayIso);
    try {
      const res = await rpc("forecast_bank_holidays_feed", { p_from: from, p_to: to });
      if (!res || res.error || !res.data) throw new Error(res && res.error ? res.error.message : "empty feed");
      bankHolidays = holidaysFromFeed(res.data);
      cache.bhDay = todayIso; cache.bhDates = bankHolidays;   // memo only a good answer; a failure is retried next build
    } catch (e) {
      if (!cache.warnedBh) { warn("[forecast-signals] bank-holiday feed unavailable — no bank-holiday flag this build: " + (e?.message || e)); cache.warnedBh = true; }
      bankHolidays = [];
    }
  }

  // 2. Weather — the stored reading for this shop+day, if the importer has one.
  let storedRow = null;
  try {
    const res = await rpc("forecast_weather_feed", { p_site: site, p_from: date, p_to: date });
    const days = res && !res.error && res.data && Array.isArray(res.data.days) ? res.data.days : [];
    storedRow = days.find((d) => d && String(d.day).slice(0, 10) === String(date).slice(0, 10)) || null;
  } catch (e) { storedRow = null; }

  const { weather, result } = await resolveWeather({ date, storedRow, fetchDay, now });

  return {
    config: configWithSignals(DEFAULT_CONFIG, { bankHolidays, weather }),
    bankHolidays,
    weather,
    weatherResult: result,
    weatherSource: storedRow ? "supabase" : (result && result.status === "ok" ? "open-meteo" : result ? result.status : "none"),
  };
}
